'use client'

import { useDisclosure } from '@nextui-org/react'
import { useLocale } from 'next-intl'
import { useCallback, useState } from 'react'

import { fetchCommon } from '@/libs/actions/common.action'

export interface IMaintenance {
  status: boolean
  message: string
}

/**
 * `/api-v2/common` answers `{CSRF_TOKEN, MAINTENANCE}` on success but `{statusCode, message}` from
 * its catch path, so the field cannot be assumed present — reading it blind is a TypeError on every
 * upstream failure. `message` is already localised by the API (the route forwards `CONTENT_LANG`).
 */
export function readMaintenance(payload: unknown): IMaintenance | null {
  const value = (payload as { MAINTENANCE?: unknown } | null | undefined)?.MAINTENANCE

  if (!value || typeof value !== 'object') return null

  const { status, message } = value as { status?: unknown; message?: unknown }

  return { status: status === true, message: typeof message === 'string' ? message : '' }
}

/**
 * Gate for the actions a maintenance window takes offline. `check()` is the gate — await it before
 * the action and abandon the action when it answers true, at which point the modal is already open:
 *
 *     if (await check()) return
 *
 * Fails OPEN. An unreachable or malformed response answers false and the action proceeds, because
 * the alternative kills every gated CTA on the site whenever this one call blips — worse than
 * letting a request reach an API that will refuse it itself.
 */
export function useMaintenance() {
  const locale = useLocale()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [message, setMessage] = useState('')

  const check = useCallback(async (): Promise<boolean> => {
    try {
      const maintenance = readMaintenance(await fetchCommon(locale))

      if (!maintenance?.status) return false

      setMessage(maintenance.message)
      onOpen()

      return true
    } catch (error) {
      console.error('[maintenance] status check failed', error)

      return false
    }
  }, [locale, onOpen])

  return { isBlocked: isOpen, message, check, close: onClose }
}
